import {
    QueryClient,
    useMutation,
    useQuery,
    useQueryClient,
    useInfiniteQuery
} from '@tanstack/react-query';
import {
    Creative,
    SupportedCountry,
    complianceNameToCodeId,
    type ComplianceCodeName,
    createPollingGenerator
} from 'bigdatr-style';
import type {
    ComplianceShortlistCreativeQuery,
    ComplianceCsvInput,
    CreativeItemV3,
    ComplianceCreativeListSort
} from '~/graphql-react-query/graphql';
import {QueryStringFilters} from '~/feature/filter/types';
import useClientMainAuthorizedFetch from '../clientMainAuthorisedFetch';
import {ComplianceCreativeItem as ComplianceCreativeItemQuery} from '../creative/graphqlQueries';
import ensureDatesAreWithinTrialLimits from '~/creative/util/ensureDatesAreWithinTrialLimits';
import {applyComplianceExploreCreativeDateFloor} from './complianceExploreConstants';
import {useCountry} from '~/feature/country/CountryContext';
import {
    ComplianceAddToShortlist,
    ComplianceCreativeList,
    ComplianceCsv,
    ComplianceRemoveFromShortlist,
    ComplianceShortlistCreative as ComplianceShortlistCreativeQueryDocument,
    ComplianceShortlistCreatives,
    ComplianceShortlists
} from './graphqlQueries';
import {complianceShortlistQueryKeys} from './helpers';
import useEarliestSelectableCreativeDate from '~/affordance/dates/useEarliestSelectableDate';

type ComplianceShortlistCreative = NonNullable<
    NonNullable<ComplianceShortlistCreativeQuery['compliance']>['creative']
>;
type ComplianceShortlistCreativeMutationArgs = {
    complianceList: ComplianceCodeName;
    creativeId: string;
    shortlist: string;
};

type ComplianceDataRow = NonNullable<CreativeItemV3['compliance']>[number];

function findComplianceForActiveCode(
    complianceFromCreative: CreativeItemV3['compliance'],
    activeComplianceCodeId: string
): ComplianceDataRow | undefined {
    return complianceFromCreative?.find(
        (row) => row?.compliance_code_id === activeComplianceCodeId
    );
}

async function invalidateComplianceShortlistQueries(
    queryClient: QueryClient,
    args: ComplianceShortlistCreativeMutationArgs
) {
    const complianceCodeId = complianceNameToCodeId(args.complianceList);

    await Promise.all([
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.lists(complianceCodeId)
        }),
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.creatives(complianceCodeId, args.shortlist)
        }),
        queryClient.invalidateQueries({
            queryKey: complianceShortlistQueryKeys.creative(complianceCodeId, args.creativeId)
        })
    ]);
}

export function useComplianceResults(args: {
    complianceCodeName: ComplianceCodeName;
    filter?: QueryStringFilters;
    orderBy: ComplianceCreativeListSort;
    pageNumber: number;
    enabled?: boolean;
}) {
    const {orderBy, pageNumber, complianceCodeName, enabled = true} = args;
    const {isComplianceOnTrial} = useCountry();

    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'compliance'});
    const request = useClientMainAuthorizedFetch();

    const {creativeDateRange, ...otherFilters} = args.filter ?? {};
    const dateWithinLimitations = applyComplianceExploreCreativeDateFloor(
        ensureDatesAreWithinTrialLimits({
            originalDate: creativeDateRange,
            isTrialing: isComplianceOnTrial,
            earliestSelectableDate
        })
    );

    const filters = {
        ...otherFilters,
        country: SupportedCountry.AU,
        dateRangeFilter: dateWithinLimitations,
        compliance_code_id: complianceNameToCodeId(complianceCodeName)
    };

    const pageSize = 20;

    return useQuery({
        enabled,
        queryKey: [
            'compliance-results',
            complianceCodeName,
            otherFilters,
            filters,
            orderBy,
            pageNumber,
            pageSize
        ],
        queryFn: async () => {
            const response = await request(ComplianceCreativeList, {
                input: {
                    filters,
                    orderBy,
                    pagination: {
                        count: pageSize,
                        page: pageNumber
                    }
                }
            });
            const rawResponse = response.compliance?.creativeList;
            if (!rawResponse) throw new Error('Compliance creative list not returned from api');

            const {items, paginationInfo} = rawResponse;
            const activeComplianceCodeId = complianceNameToCodeId(complianceCodeName);
            const creatives = items.map((item) =>
                mapOnlyActiveCodeToCreative(item, activeComplianceCodeId)
            );

            return {
                creatives,
                hasNextPage: paginationInfo.hasNextPage,
                hasPreviousPage: paginationInfo.page > 1
            };
        }
    });
}

export function useInfiniteComplianceResults(args: {
    complianceCodeName: ComplianceCodeName;
    filter?: QueryStringFilters;
    orderBy: ComplianceCreativeListSort;
    pageNumber: number;
    enabled?: boolean;
}) {
    const {orderBy, pageNumber, complianceCodeName, enabled = true} = args;
    const {isComplianceOnTrial} = useCountry();

    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'compliance'});
    const request = useClientMainAuthorizedFetch();

    const {creativeDateRange, ...otherFilters} = args.filter ?? {};
    const dateWithinLimitations = applyComplianceExploreCreativeDateFloor(
        ensureDatesAreWithinTrialLimits({
            originalDate: creativeDateRange,
            isTrialing: isComplianceOnTrial,
            earliestSelectableDate
        })
    );

    const filters = {
        ...otherFilters,
        country: SupportedCountry.AU,
        dateRangeFilter: dateWithinLimitations,
        compliance_code_id: complianceNameToCodeId(complianceCodeName)
    };

    const pageSize = 20;

    return useInfiniteQuery({
        enabled,
        queryKey: [
            'infinite-compliance-results',
            complianceCodeName,
            otherFilters,
            filters,
            orderBy,
            pageNumber,
            pageSize
        ],
        queryFn: async ({pageParam}) => {
            const response = await request(ComplianceCreativeList, {
                input: {
                    filters,
                    orderBy,
                    pagination: {
                        count: pageSize,
                        page: pageParam
                    }
                }
            });
            const rawResponse = response.compliance?.creativeList;
            if (!rawResponse)
                throw new Error('Compliance infinite creative list not returned from api');

            const {items, paginationInfo} = rawResponse;
            const activeComplianceCodeId = complianceNameToCodeId(complianceCodeName);
            const creatives = items.map((item) =>
                mapOnlyActiveCodeToCreative(item, activeComplianceCodeId)
            );

            return {
                creatives,
                hasNextPage: paginationInfo.hasNextPage,
                hasPreviousPage: paginationInfo.page > 1,
                currentPage: pageParam
            };
        },
        initialPageParam: pageNumber,
        getNextPageParam: (lastPage) => {
            const hasNextPage = lastPage.hasNextPage;
            const page = lastPage.currentPage;
            if (hasNextPage && page) return page + 1;
            return null;
        }
    });
}

export function useComplianceCreative(args: {
    creativeId: string;
    complianceCodeName: ComplianceCodeName;
    enabled?: boolean;
}) {
    const {creativeId, complianceCodeName, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();

    return useQuery({
        enabled,
        queryKey: ['compliance-creative', creativeId, complianceCodeName],
        queryFn: async () => {
            const response = await request(ComplianceCreativeItemQuery, {creativeId});
            const rawCreative = response.creativeV2?.publicCreativeItemV2;
            if (!rawCreative) throw new Error('Creative not returned from api');

            return mapOnlyActiveCodeToCreative(
                rawCreative,
                complianceNameToCodeId(complianceCodeName)
            );
        }
    });
}

export function useComplianceShortlists(args: {
    complianceList: ComplianceCodeName;
    enabled?: boolean;
}) {
    const {complianceList, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.lists(complianceCodeId),
        queryFn: async () => {
            const response = await request(ComplianceShortlists, {
                input: {complianceCodeId}
            });
            const shortlists = response.compliance?.shortlists;
            if (!shortlists) throw new Error('Compliance shortlists not returned from api');
            return shortlists;
        }
    });
}

export function useComplianceShortlistCreatives(args: {
    complianceList: ComplianceCodeName;
    shortlist: string;
    enabled?: boolean;
}) {
    const {complianceList, shortlist, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.creatives(complianceCodeId, shortlist),
        queryFn: async () => {
            const response = await request(ComplianceShortlistCreatives, {
                complianceCodeId,
                shortlist
            });
            const creatives = response.compliance?.creatives;
            if (!creatives) {
                throw new Error('Compliance shortlist creatives not returned from api');
            }
            return creatives;
        }
    });
}

export function useComplianceShortlistCreative(args: {
    complianceList: ComplianceCodeName;
    creativeId: string;
    enabled?: boolean;
}) {
    const {complianceList, creativeId, enabled = true} = args;
    const request = useClientMainAuthorizedFetch();
    const complianceCodeId = complianceNameToCodeId(complianceList);

    return useQuery({
        enabled,
        queryKey: complianceShortlistQueryKeys.creative(complianceCodeId, creativeId),
        queryFn: async (): Promise<ComplianceShortlistCreative> => {
            const response = await request(ComplianceShortlistCreativeQueryDocument, {
                complianceCodeId,
                creativeId
            });
            const creative = response.compliance?.creative;
            if (!creative) {
                throw new Error('Compliance shortlist creative not returned from api');
            }
            return creative;
        }
    });
}

export function useComplianceAddToShortlist() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: ComplianceShortlistCreativeMutationArgs) => {
            const {complianceList, creativeId, shortlist} = args;
            const response = await request(ComplianceAddToShortlist, {
                input: {
                    complianceCodeId: complianceNameToCodeId(complianceList),
                    creativeId,
                    shortlist
                }
            });
            return response.compliance?.addToShortlist ?? null;
        },
        onSuccess: async (_data, args) => {
            await invalidateComplianceShortlistQueries(queryClient, args);
        }
    });
}

export function useComplianceRemoveFromShortlist() {
    const request = useClientMainAuthorizedFetch();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (args: ComplianceShortlistCreativeMutationArgs) => {
            const {complianceList, creativeId, shortlist} = args;
            const response = await request(ComplianceRemoveFromShortlist, {
                input: {
                    complianceCodeId: complianceNameToCodeId(complianceList),
                    creativeId,
                    shortlist
                }
            });
            return response.compliance?.removeFromShortlist ?? null;
        },
        onSuccess: async (_data, args) => {
            await invalidateComplianceShortlistQueries(queryClient, args);
        }
    });
}

export function useComplianceCsv() {
    const request = useClientMainAuthorizedFetch();
    const {isComplianceOnTrial} = useCountry();

    const earliestSelectableDate = useEarliestSelectableCreativeDate({type: 'compliance'});

    return useMutation({
        mutationFn: async (input: ComplianceCsvInput) => {
            const pollingData = createPollingGenerator({
                createRequest: async () => {
                    const {dateRangeFilter, ...otherFilters} = input.filters;

                    const sanitizedDate = applyComplianceExploreCreativeDateFloor(
                        ensureDatesAreWithinTrialLimits({
                            originalDate: dateRangeFilter ?? undefined,
                            isTrialing: isComplianceOnTrial,
                            earliestSelectableDate
                        })
                    );

                    const response = await request(ComplianceCsv, {
                        input: {
                            ...input,
                            filters: {
                                ...otherFilters,
                                dateRangeFilter: sanitizedDate
                            }
                        }
                    });
                    return response.compliance?.csv;
                },
                pollRequest: async (response) => {
                    const {csv, errorJson} = response || {};
                    if (!csv || !errorJson) {
                        throw new Error('CSV or error JSON signed URLsnot returned from API');
                    }

                    // make a request to the signed URLs and return their statuses
                    const [csvResponse, errorJsonResponse] = await Promise.all([
                        fetch(csv),
                        fetch(errorJson)
                    ]);

                    const hasError = errorJsonResponse.status === 200;
                    const isCsvFileReady = csvResponse.status === 200;
                    return {
                        csv,
                        errorJson,
                        isCsvFileReady,
                        downloadStatus: csvResponse.status,
                        errorStatus: errorJsonResponse.status,
                        hasError
                    };
                },
                pollWhile: ({isCsvFileReady, hasError}) => {
                    // stop polling if an error file shows up, or if the csv file shows up
                    return !hasError && !isCsvFileReady;
                },
                interval: 2000
            });

            let lastPayload: Awaited<ReturnType<(typeof pollingData)['next']>>['value'] | null =
                null;
            for await (const payload of pollingData) {
                lastPayload = payload;
                // if an error file exists at the given URL, fetch the JSON with the error message
                // and expose it to the user by throwing it
                if (payload.hasError) {
                    const errorResponse = await fetch(payload.errorJson);
                    const json = await errorResponse.json();
                    const error = new Error(json.error);
                    error.name = json.name;
                    error.stack = json.stack;
                    throw error;
                }
            }
            return lastPayload;
        }
    });
}

function mapOnlyActiveCodeToCreative(
    item: CreativeItemV3,
    activeComplianceCodeId: string
): Creative {
    const {compliance, ...rest} = item;
    const row = findComplianceForActiveCode(compliance, activeComplianceCodeId);
    return new Creative({
        ...rest,
        complianceRiskRating: row?.compliance_risk_rating ?? null,
        complianceReason: row?.compliance_reason ?? null
    });
}
